import { createContext, use } from 'react'
import { NumberField as BaseNumberField } from '@base-ui/react/number-field'

import { cn } from '@gto/ui/cn'
import type { InputProps } from '@gto/ui/input'
import { InputAddon, InputGroup, inputVariants } from '@gto/ui/input'

import { applyTypedDecimalSeparator } from './decimal-separator'

type BaseNumberFieldRootProps = BaseNumberField.Root.Props

type BaseNumberFieldInputProps = BaseNumberField.Input.Props

// Base UI parses the typed text with the Root `locale`, but the Input never sees
// the Root props. The Root passes the same locale down, so the Input maps a typed
// separator to the decimal separator that Base UI parses.
const NumberInputLocaleContext = createContext<BaseNumberFieldRootProps['locale']>(undefined)

const NumberInputRoot = (props: BaseNumberFieldRootProps) => {
  return (
    <NumberInputLocaleContext value={props.locale}>
      <BaseNumberField.Root {...props} />
    </NumberInputLocaleContext>
  )
}

const NumberInputControl = ({ onChange, ...props }: BaseNumberFieldInputProps) => {
  const locale = use(NumberInputLocaleContext)

  return (
    <BaseNumberField.Input
      {...props}
      onChange={(event) => {
        // Base UI runs its own change handler after this one and reads the input
        // text, so the separator must be fixed in the DOM before this returns.
        if (
          applyTypedDecimalSeparator(event.currentTarget, event.nativeEvent, locale) === 'rejected'
        ) {
          event.preventBaseUIHandler()

          return
        }

        onChange?.(event)
      }}
    />
  )
}

export type NumberInputFieldProps = Omit<BaseNumberFieldInputProps, 'render' | 'style' | 'size'> &
  InputProps

const NumberInputField = ({
  className,
  variant,
  size,
  textAlign,
  id,
  startAddon,
  endAddon,
  ...props
}: NumberInputFieldProps) => {
  const { input } = inputVariants({ size, variant, textAlign })

  return (
    <InputGroup
      className={className}
      endAddon={endAddon}
      size={size}
      startAddon={startAddon}
      variant={variant}
    >
      <NumberInputControl
        className={input()}
        data-slot="input-group-control"
        data-tst="number-input"
        id={id}
        {...props}
      />
    </InputGroup>
  )
}

export type NumberInputProps = Omit<BaseNumberFieldRootProps, 'render' | 'className'> &
  Omit<InputProps, 'value' | 'defaultValue' | 'onChange'>

const NumberInputBase = ({
  className,
  variant,
  size,
  textAlign,
  id,
  startAddon = null,
  endAddon = null,
  min,
  max,
  step,
  largeStep,
  smallStep,
  value,
  defaultValue,
  onValueChange,
  allowWheelScrub,
  disabled,
  readOnly,
  required,
  name,
  format,
  locale,
  autoFocus,
  ...props
}: NumberInputProps) => {
  const { root, input } = inputVariants({ size, variant, textAlign })

  const rootProps = {
    min,
    max,
    step,
    largeStep,
    smallStep,
    value,
    defaultValue,
    onValueChange,
    allowWheelScrub,
    disabled,
    readOnly,
    required,
    name,
    format,
    locale,
    autoFocus,
  }

  return (
    <NumberInputRoot
      className={cn(root(), className)}
      {...rootProps}
    >
      {startAddon !== null && <InputAddon data-slot="start-addon">{startAddon}</InputAddon>}
      <NumberInputControl
        className={input()}
        data-slot="input-group-control"
        data-tst="number-input"
        id={id}
        {...props}
      />
      {endAddon !== null && <InputAddon data-slot="end-addon">{endAddon}</InputAddon>}
    </NumberInputRoot>
  )
}

export const NumberInput = Object.assign(NumberInputBase, {
  Root: NumberInputRoot,
  Input: NumberInputField,
  Group: BaseNumberField.Group,
  Increment: BaseNumberField.Increment,
  Decrement: BaseNumberField.Decrement,
  ScrubArea: BaseNumberField.ScrubArea,
  ScrubAreaCursor: BaseNumberField.ScrubAreaCursor,
})
